/**
 * §A.7 · El directorio público de clubes.
 *
 * ## Las dos alarmas de esta sección, que no son cifras de vanidad
 *
 * `hours_waiting` en una reclamación: «un maestro que reclama su ficha un martes y
 * sigue esperando el viernes ya no vuelve». Y `still_listed` en una petición de
 * retirada, que el contrato llama con estas palabras **una alarma, no un estado
 * normal**: la ficha se esconde al recibir la petición, así que un `true` ahí
 * significa que la retirada inmediata que prometimos no ocurrió.
 *
 * ## `listed` frente a `customers` van juntas
 *
 * «Un directorio que crece y una cartera que no significa que el buscador atrae
 * visitas y no convierte, que es una conversación distinta de "hay que sembrar más".»
 * Por eso la pantalla las pone al lado y no en dos bloques.
 *
 * ## `skipped` en una importación no es un fallo
 *
 * «La fuente se importa varias veces y volver a pasarla no puede duplicar clubes ni
 * pisar fichas: alguno puede haber reclamado la suya desde la última pasada, y
 * sobrescribirla sería borrarle el trabajo.»
 */

export interface EstadisticasDelDirectorio {
  readonly listed: number;
  readonly seeded: number;
  readonly claim_pending: number;
  readonly claimed: number;
  readonly hidden: number;
  readonly customers: number;
  readonly listed_customers: number;
  readonly venues_geocoded: number;
  /** Sin coordenadas no sale en una búsqueda por cercanía: sembrado y invisible. */
  readonly venues_pending_geocode: number;
  readonly trial_bookings: number;
  readonly trial_bookings_last_30d: number;
  readonly bookings_converted: number;
  readonly removal_requests_open: number;
  readonly by_province: readonly {
    readonly province: string;
    readonly listed: number;
    readonly customers: number;
  }[];
}

export interface Reclamacion {
  readonly id: string;
  readonly academy_id: string;
  readonly academy_slug: string;
  readonly academy_name: string;
  readonly claimant_user_id: string;
  readonly claimant_email: string;
  readonly claimant_name: string;
  readonly evidence: Record<string, unknown>;
  readonly status: 'pending' | 'approved' | 'rejected';
  readonly decided_by: string | null;
  readonly decided_at: Date | string | null;
  readonly rejection_reason: string | null;
  readonly created_at: Date | string;
  /** Horas esperando, mientras siga pendiente. Ver la cabecera. */
  readonly hours_waiting: number | null;
}

export interface PeticionDeBaja {
  readonly id: string;
  readonly academy_id: string;
  readonly academy_slug: string;
  readonly academy_name: string;
  readonly requester_name: string | null;
  readonly requester_email: string | null;
  readonly requester_role: string | null;
  readonly reason: string | null;
  readonly status: 'received' | 'contacted' | 'reopened';
  readonly received_at: Date | string;
  readonly handled_at: Date | string | null;
  readonly note: string | null;
  readonly hours_waiting: number | null;
  /** **Una alarma, no un estado normal.** Ver la cabecera. */
  readonly still_listed: boolean;
}

export interface ResultadoDeImportacion {
  readonly source: string;
  readonly created: number;
  /** No es un fallo: es la idempotencia de la fuente. Ver la cabecera. */
  readonly skipped: number;
  readonly venues_created: number;
  /** Las creadas que ya salen en el buscador (02-11-2026). */
  readonly published: number;
  readonly errors: readonly {
    readonly slug: string;
    readonly reason: string;
  }[];
}

/**
 * Horas esperando, en algo que se dice por teléfono.
 *
 * «Lleva 3 días» se entiende; «lleva 74 horas» hay que dividirlo. Por debajo del día
 * las horas sí son la unidad útil, porque la promesa se mide en horas.
 */
export function esperaEnPalabras(horas: number | null): string {
  if (horas === null) return '—';
  if (horas < 1) return 'menos de una hora';
  if (horas < 24) return horas === 1 ? 'una hora' : `${horas} horas`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'un día' : `${dias} días`;
}

/**
 * Los tres estados de una petición de retirada, **copiados del DTO**.
 *
 * `reopened` es el que no se adivina: alguien pidió la baja, se atendió, y la ficha
 * volvió a aparecer —una reimportación del censo, por ejemplo—. Es el caso que hace
 * falta ver, así que tiene nombre propio y no cae en un `?? estado`.
 */
export const etiquetaDeBaja = (estado: string): string =>
  ({
    received: 'Recibida',
    contacted: 'Contactado',
    reopened: 'Volvió a aparecer',
  })[estado] ?? estado;
